/**
 * perkie-pdp-qr.js - port step 38-B (D23 BET 07): the phone handoff panel
 * under the 3C card on the desktop PDP, 750px and up. Loaded (deferred) by
 * sections/main-product.liquid only where D23 applies (the section carries
 * the perkie_previewer block: templates/product.custom.json). Markup:
 * snippets/perkie-pdp-qr.liquid. CSS: the 38-B block in perkie-pdp-d23.css.
 *
 * Canon: redesign-brief/canon-export-2026-08-09/Desktop Compositions - PDP
 * and Cart Page.dc.html (DESIGN FINAL 2026-10-07, md5 e737a847...), the
 * showQr block and its QR table: pre / waiting / arrived.
 *
 * What it does
 *  - Shows the panel while the card holds a session to hand over and no
 *    photo yet: the one-pet photo step with the slot empty (port step 38-D-B,
 *    ruling (b), ledger 2026-10-11 PM-39: no longer the count step, and the
 *    38-C mint moved with it), and never on the device that scanned a code
 *    (flow3c-state handoff). Never below 750, never on a memorial session, never with no
 *    session (a first visit has no perkie_sid until its first upload, so
 *    there is no existing link to encode: gap, 38-B report).
 *  - Draws the QR code client-side (assets/perkie-qrcodegen.js, vendored,
 *    MIT, loaded on demand the first time a code is needed). It encodes
 *    the existing resume link and nothing else: the product URL plus
 *    ?perkie_sid=<the card's session>. A phone that opens it adopts that
 *    session (frontend/src/services/session-provider.jsx reads perkie_sid
 *    first), so its upload lands in the session the card holds. Port step
 *    38-D-A: plus a handoff marker, the fragment #m / #e (linkFor below), so
 *    the phone keeps that session to the one tab and its own stays stored.
 *  - "pre" is the resting state. The artifact's trigger for "waiting" ("the
 *    customer chose the phone") is not something the page can observe
 *    (gap), so waiting is the fallback the brief defines: the foreground
 *    poll below stopped (its 10-minute cap) or the tab was hidden while
 *    the photo-step panel showed.
 *  - "I've sent it" asks the card to re-read its session
 *    (perkie:flow3c-reread, the bundle hook approved 2026-10-07); the card
 *    seats a photo the phone sent into its photo slot.
 *  - Part 2, "arrived" (retires FLAG D23-02's ENGINE-PENDING): while the
 *    panel is on screen, the tab is in the foreground and the card is on
 *    the one-pet photo step with the slot empty, it asks the card for the
 *    same re-read every 5 seconds (the card GETs /api/v2/session/:id; that
 *    route has no rate limiter and draws on no upload or generation quota,
 *    so the poll can never 429 the card; each read costs one Redis GET and
 *    two Postgres reads, hence the foreground-only rule, the 10-minute
 *    cap, the error backoff to 30 seconds and one read at a time). When
 *    the card seats a photo the phone sent (flow3c-state phonePhoto), the
 *    panel draws "arrived": one sentence, no code, the warm ground.
 *  - Port step 38-C (Corey 2026-10-11, ledger PM-32): a first-time visitor's
 *    card holds no session, so when the panel is about to show for one (the
 *    conditions above, plus the card itself on screen) this asks the card,
 *    ONCE per page view, for a handoff id through the same re-read event
 *    (source "handoff"). The card mints an empty session (perkie-api POST
 *    /api/v2/session) and answers {status: "handoff", handoffId}; the code
 *    then encodes ?perkie_sid=<handoff id> and the poll / "I've sent it"
 *    re-read it. When the phone's photo is seated the card adopts that
 *    session and its next flow3c-state carries it as sessionId. Any other
 *    answer, or none, leaves the panel hidden, exactly as before 38-C.
 *
 * Events
 *  - LISTENS perkie:flow3c-state (bubbles from the 3C card root; read-only;
 *    reads step, count, hasPhoto, sessionId, phonePhoto and, 38-D-B,
 *    handoff). Other
 *    listeners on that type: assets/perkie-pdp-d23.js (this page) and
 *    sections/perkie-reveal-hero.liquid (free preview only). Multiple
 *    listeners are the intended design; this file never proxies
 *    addEventListener and never stops propagation.
 *  - DISPATCHES perkie:flow3c-reread on document, detail {source: "tap" |
 *    "poll" | "handoff", reply(result)}. Its only listener is the PDP card
 *    (frontend/src/components/previewer/flow3c.jsx), which answers through
 *    reply({status: "arrived" | "none" | "busy" | "unseatable" |
 *    "no-session" | "gone" | "error"}); a "handoff" request (38-C) is
 *    answered {status: "handoff", handoffId} or "has-session" /
 *    "unavailable" / "unseatable".
 *  - GA4: perkie_qr_handoff {action: "shown" | "tap" | "arrived", via:
 *    "tap" | "poll" on arrived, sid: "minted" | "existing" (38-C: whether the
 *    code carries a session minted for this handoff or the card's own)}
 *    (gtag, when present). The phone side sends the same event with action
 *    "phone_opened" from the bundle (38-D-A; perkie-analytics.js
 *    trackQrPhoneOpened) and, 38-D-B, "phone_uploaded" / "phone_continued"
 *    (trackQrPhoneAction), never from this file.
 * Load order: this <script defer> sits beside perkie-pdp-d23.js near the top
 * of <product-info>, BEFORE the deferred perkie-previewer.js, so it is
 * listening before the card mounts and sends its first flow3c-state.
 * Double-load guard: the in-file flag below, nothing shared.
 */
(function () {
  "use strict";
  if (window.__perkiePdpQrLoaded) return;
  window.__perkiePdpQrLoaded = true;

  var root = document.querySelector("product-info.perkie-d23");
  var panel = root && root.querySelector("[data-perkie-pdp-qr]");
  if (!panel) return;
  var mount = root.querySelector('[data-perkie-previewer="product"]');
  var codeBox = panel.querySelector("[data-perkie-pdp-qr-code]");
  var sentBtn = panel.querySelector("[data-perkie-pdp-qr-sent]");
  var LINK = panel.getAttribute("data-perkie-qr-link") || "";
  var LIB = panel.getAttribute("data-perkie-qr-lib") || "";
  // The drawn code box is 96 x 96 with a 1px hairline and 5px padding, so
  // the code itself gets 84px (artifact: width/height 96, padding 5).
  var CODE_PX = 84;
  // The card answers a re-read within its own 10s time box; this is the
  // backstop for a page where no card is listening at all.
  var REPLY_BACKSTOP_MS = 15000;
  // The foreground poll (part 2): every 5s, doubling to at most 30s after
  // an error, and no more than 10 minutes of polling per page view.
  var POLL_MS = 5000;
  var POLL_MAX_MS = 30000;
  var POLL_CAP_MS = 10 * 60 * 1000;

  var wideMql = window.matchMedia ? window.matchMedia("(min-width: 750px)") : null;
  function isWide() { return !!(wideMql && wideMql.matches); }
  function isMemorial() {
    return root.classList.contains("perkie-canon--memorial")
      || !!(mount && mount.getAttribute("data-perkie-memorial") === "true");
  }

  // st: the last flow3c-state from the card. sid: its session. codeFor: the
  // session the drawn code encodes. gone: the session read answered 404.
  // fellBack: waiting replaces pre on the photo step (see the header).
  // capped: the poll used its 10 minutes. inView: the panel is on screen.
  var s = { st: null, sid: null, codeFor: null, drawing: null, gone: false, fellBack: false, capped: false, inView: false, shown: false, tapBusy: false };
  // 38-C. hid: the handoff id the card minted for a first visit (null until
  // it answers). asked: the one handoff request of this page view has gone
  // out. cardInView: the card (the panel's slot is directly under it) is on
  // screen. cur(): the session the code carries, the card's own first.
  s.hid = null;
  s.asked = false;
  s.cardInView = false;
  function cur() { return s.sid || s.hid; }
  // running: polling now; since: when this run started; used: ms polled
  // before it; delay: the next interval; busy: a poll read is in flight.
  var poll = { timer: 0, running: false, since: 0, used: 0, delay: POLL_MS, busy: false };

  function track(action, via) {
    try {
      if (typeof window.gtag !== "function") return;
      var p = { action: action, mount: "pdp" };
      if (via) p.via = via;
      p.sid = s.hid && cur() === s.hid ? "minted" : "existing"; // 38-C
      window.gtag("event", "perkie_qr_handoff", p);
    } catch (e) { /* analytics never reaches the customer */ }
  }

  /* ── the code ─────────────────────────────────────────────────────── */
  var lib = { state: 0, waiters: [] }; // 0 idle, 1 loading, 2 ready, 3 failed
  function withEncoder(cb) {
    if (window.PerkieQrcodegen) { cb(window.PerkieQrcodegen); return; }
    if (lib.state === 3 || !LIB) return;
    lib.waiters.push(cb);
    if (lib.state === 1) return;
    lib.state = 1;
    var sc = document.createElement("script");
    sc.src = LIB;
    sc.async = true;
    sc.onload = function () {
      var w = lib.waiters;
      lib.waiters = [];
      lib.state = window.PerkieQrcodegen ? 2 : 3;
      if (lib.state === 2) for (var i = 0; i < w.length; i++) { try { w[i](window.PerkieQrcodegen); } catch (e) { /* ignore */ } }
      render();
    };
    sc.onerror = function () { lib.state = 3; lib.waiters = []; render(); };
    document.head.appendChild(sc);
  }

  // Port step 38-D-A (ledger 2026-10-11 PM-36): the link marks itself as a
  // QR handoff, so the phone keeps the session to that tab and its own stored
  // session survives (frontend/src/services/session-provider.jsx). The marker
  // is a one-letter fragment: "#m" when the code carries a session minted for
  // this handoff (38-C), "#e" when it carries the card's existing session.
  // Two bytes, because the longest live product.custom link is 132 bytes and
  // 134 is the most a 41-module code holds (version 6, ECC LOW, byte mode),
  // the size that keeps 2px whole-pixel modules in the 84px box; and a
  // fragment is never sent to Shopify's edge, so the edge cannot strip it.
  function linkFor(sid) {
    return LINK + (LINK.indexOf("?") < 0 ? "?" : "&") + "perkie_sid=" + encodeURIComponent(sid) + (s.hid && sid === s.hid ? "#m" : "#e");
  }

  // One SVG path, one run per row of dark modules. Whole-pixel modules when
  // at least 2px fit the 84px box (every live product link is a 41-module
  // code: 2px modules, 82px), else the code fills the box at a fractional
  // module size. shape-rendering crispEdges keeps the module edges hard.
  function drawCode(sid) {
    if (!codeBox || s.drawing === sid) return;
    s.drawing = sid;
    withEncoder(function (enc) {
      if (cur() !== sid) return;
      var q;
      try { q = enc.encode(linkFor(sid)); } catch (e) { s.drawing = null; return; }
      var n = q.size, d = "";
      for (var y = 0; y < n; y++) {
        var x = 0;
        while (x < n) {
          if (!q.dark(x, y)) { x++; continue; }
          var x0 = x;
          while (x < n && q.dark(x, y)) x++;
          d += "M" + x0 + " " + y + "h" + (x - x0) + "v1h-" + (x - x0) + "z";
        }
      }
      var unit = Math.floor(CODE_PX / n);
      var px = unit >= 2 ? unit * n : CODE_PX;
      codeBox.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + n + " " + n + '" width="' + px + '" height="' + px
        + '" shape-rendering="crispEdges" focusable="false"><path fill="currentColor" d="' + d + '"/></svg>';
      s.codeFor = sid;
      render();
    });
  }

  /* ── which state, and whether it shows ───────────────────────────── */
  // Before a photo (the artifact's "Before a photo" / "Phone handoff"
  // scenarios): the one-pet photo step with no photo (38-D-B: the count step
  // is DIVERGED-BY-RULING (b) from the D23 QR table, below).
  // Multi-pet photo steps are undrawn for the handoff (gap): no panel there.
  // Part 2: the one-pet photo step also keeps the panel while its slot holds
  // the photo the card seated from the phone ("Photo arrived").
  // Port step 38-D-B, ruling (b) (Corey 2026-10-11, ledger PM-39, inside the
  // 3C artifact's D25 rows): never the count step any more, so a code exists
  // only where the card seats the phone's photo. And never on the device
  // that scanned a code (flow3c-state handoff, D25 scope: "At 750+ in
  // handoff mode the QR panel does not draw on the scanning device").
  function wanted(st) {
    if (!st || st.handoff || !cur() || s.gone || !isWide() || isMemorial()) return false;
    return st.step === 2 && st.count === 1 && (!st.hasPhoto || st.phonePhoto);
  }
  // 38-C: the panel would show for this state but the card holds no session
  // to encode. Belt and braces on memorial: the tab key and the URL too.
  // 38-D-B: the mint moves with the panel (ruling (b)): the one-pet photo
  // step with the slot empty only, and never in handoff mode.
  function needsHandoff(st) {
    if (!st || st.handoff || cur() || s.asked || s.gone || !s.cardInView || !isWide() || isMemorial()) return false;
    try { if (window.sessionStorage.getItem("perkie_memorial_context") === "1") return false; } catch (e) { /* ignore */ }
    if (/(?:^|[?&])context=memorial(?:&|$)/.test(window.location.search || "")) return false;
    return st.step === 2 && st.count === 1 && !st.hasPhoto;
  }
  function askHandoff() {
    s.asked = true; // once per page view, whatever the answer
    // On the next task, never inside the card's own state dispatch: the card
    // attaches its re-read listener in an effect that runs after the one
    // that sends its first flow3c-state, so a synchronous ask would be lost.
    setTimeout(function () {
      reread("handoff", function (r) {
        if (r && r.status === "handoff" && typeof r.handoffId === "string" && r.handoffId && !s.sid) s.hid = r.handoffId;
        render();
      });
    }, 0);
  }
  function stateFor(st) {
    // Not the photo step: pre (since 38-D-B (b) the panel never shows there).
    if (!st || st.step !== 2) return "pre";
    if (st.phonePhoto) return "arrived";
    return s.fellBack ? "waiting" : "pre";
  }

  function render() {
    var st = s.st;
    if (needsHandoff(st)) askHandoff();
    var want = wanted(st);
    var state = stateFor(st);
    if (want && state !== "arrived" && s.codeFor !== cur()) drawCode(cur());
    // Never an empty code box: pre and waiting wait for their code; arrived
    // draws no code.
    var vis = want && (state === "arrived" || s.codeFor === cur());
    if (panel.getAttribute("data-state") !== state) panel.setAttribute("data-state", state);
    if (panel.hidden !== !vis) panel.hidden = !vis;
    if (vis && !s.shown) { s.shown = true; track("shown"); }
    syncPoll();
  }

  /* ── part 2: the foreground poll ─────────────────────────────────── */
  // Only where a photo can be seated, only while the panel is on screen and
  // the tab is in the foreground, under the cap, never after a 404.
  function canPoll() {
    var st = s.st;
    return !panel.hidden && !!st && st.step === 2 && st.count === 1 && !st.hasPhoto
      && s.inView && document.visibilityState === "visible" && !s.gone && !s.capped;
  }
  function syncPoll() {
    var want = canPoll();
    if (want && !poll.running) {
      poll.running = true;
      poll.since = Date.now();
      poll.delay = POLL_MS;
      schedule(0); // an immediate read: she may be back from her phone
    } else if (!want && poll.running) {
      stopPoll();
    }
  }
  function tick() {
    poll.timer = 0;
    if (!poll.running) return;
    if (poll.used + (Date.now() - poll.since) >= POLL_CAP_MS) {
      // The cap: stop for good and fall back to waiting, with its button.
      stopPoll();
      s.capped = true;
      s.fellBack = true;
      render();
      return;
    }
    if (poll.busy) { schedule(poll.delay); return; }
    poll.busy = true;
    reread("poll", function (r) {
      poll.busy = false;
      poll.delay = r.status === "error" ? Math.min(poll.delay * 2, POLL_MAX_MS) : POLL_MS;
      onReply(r, "poll");
      if (poll.running && !poll.timer) schedule(poll.delay);
    });
  }
  function stopPoll() {
    clearTimeout(poll.timer);
    poll.timer = 0;
    if (!poll.running) return;
    poll.used += Date.now() - poll.since;
    poll.running = false;
  }
  function schedule(ms) {
    clearTimeout(poll.timer);
    poll.timer = setTimeout(tick, ms);
  }

  /* ── asking the card to re-read its session ──────────────────────── */
  function reread(source, cb) {
    var called = false, backstop = 0;
    var answer = function (r) {
      if (called) return;
      called = true;
      if (backstop) clearTimeout(backstop);
      cb(r || {});
    };
    try {
      document.dispatchEvent(new CustomEvent("perkie:flow3c-reread", { detail: { source: source, reply: answer } }));
    } catch (e) { answer({ status: "error" }); return; }
    if (!called) backstop = setTimeout(function () { answer({ status: "error" }); }, REPLY_BACKSTOP_MS);
  }
  function onReply(r, via) {
    // 404: the session is gone, so the link would hand over nothing.
    if (r.status === "gone") s.gone = true;
    // The card seated the phone's photo. Its next flow3c-state (hasPhoto +
    // phonePhoto) turns the panel to arrived and stops the poll.
    if (r.status === "arrived") track("arrived", via);
    render();
  }

  if (sentBtn) {
    sentBtn.addEventListener("click", function () {
      if (s.tapBusy) return; // one re-read per tap at a time, never queued
      s.tapBusy = true;
      track("tap");
      reread("tap", function (r) { s.tapBusy = false; onReply(r, "tap"); });
    });
  }

  /* ── listeners ───────────────────────────────────────────────────── */
  document.addEventListener("perkie:flow3c-state", function (e) {
    var d = e && e.detail;
    if (!d || d.mount !== "pdp") return;
    if (mount && e.target && e.target !== document && e.target.nodeType === 1 && !mount.contains(e.target)) return;
    s.st = { step: d.step, count: d.count, hasPhoto: !!d.hasPhoto, phonePhoto: !!d.phonePhoto, handoff: !!d.handoff };
    var sid = typeof d.sessionId === "string" && d.sessionId ? d.sessionId : null;
    if (sid !== s.sid) { s.sid = sid; s.gone = false; }
    render();
  });

  // The brief's fallback (ledger 2026-10-07 PM-7): when the tab hides while
  // the photo-step panel shows, the poll stops and the panel comes back as
  // waiting, with its button. Back in the foreground the poll resumes with
  // an immediate read (inside the 10-minute cap); waiting stays.
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "hidden" && !panel.hidden && s.st && s.st.step === 2 && !s.st.phonePhoto) s.fellBack = true;
    render();
  });

  // "While the panel is visible": on screen, measured by an observer; with
  // no IntersectionObserver the panel counts as on screen whenever shown.
  if ("IntersectionObserver" in window) {
    new IntersectionObserver(function (entries) {
      var on = false;
      for (var i = 0; i < entries.length; i++) if (entries[i].isIntersecting) on = true;
      s.inView = on;
      render();
    }).observe(panel);
    // 38-C: a hidden panel has no box to observe, so "about to be on screen"
    // is read off the card it sits directly under.
    if (mount) {
      new IntersectionObserver(function (entries) {
        var on = false;
        for (var i = 0; i < entries.length; i++) if (entries[i].isIntersecting) on = true;
        s.cardInView = on;
        render();
      }).observe(mount);
    }
  } else {
    s.inView = true;
    s.cardInView = true;
  }

  if (wideMql) {
    if (wideMql.addEventListener) wideMql.addEventListener("change", render);
    else if (wideMql.addListener) wideMql.addListener(render);
  }
})();
